import { useMemo, useState } from 'react';
import { Calendar } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import type { Option } from '@/constants/institute';
import { useApiQuery } from '@/hooks/useApiQuery';
import type { DayKind, StaffAttendanceDay } from '@/types/staffAttendance';
import { cn } from '@/lib/utils';
import { teacherApi } from '../../api/teacher.api';
import { formatInstituteDate } from '../../utils';

/**
 * The teacher's own attendance, read from the staff register.
 *
 * Four states, not three: a punch is `PRESENT` once the office has approved it,
 * `PENDING` until somebody looks, `REJECTED` if they refuse it, and a past
 * working day with no punch at all is an absence. Those are the only states
 * `staff_attendance` holds — the institute records arrival, not half days or
 * late marks — so those are the four this card counts, beside days on leave
 * the office granted.
 *
 * Two months are read rather than one: a week and the last thirty days both
 * run back across the first of the month, and a window that quietly stopped
 * there would report a teacher absent for days they signed in.
 */

type Timeframe = 'this_week' | 'this_month' | 'last_30_days';

const TIMEFRAME_OPTIONS: Option<Timeframe>[] = [
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_30_days', label: 'Last 30 Days' },
];

/** `YYYY-MM-DD`, moved by whole days. Walked in UTC, as these dates are stored. */
function shiftDay(day: string, delta: number): string {
  const date = new Date(`${day}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + delta);

  return date.toISOString().slice(0, 10);
}

/** The Monday of a day's week — an Indian institute prints its week Monday first. */
function mondayOf(day: string): string {
  const date = new Date(`${day}T00:00:00.000Z`);
  // getUTCDay: 0 = Sunday, so Sunday steps back six days rather than one.
  return shiftDay(day, -((date.getUTCDay() + 6) % 7));
}

/** The month before a `YYYY-MM`. */
function previousMonthOf(month: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  return new Date(Date.UTC(year, monthNumber - 2, 1)).toISOString().slice(0, 7);
}

function windowOf(timeframe: Timeframe, today: string): { from: string; to: string } {
  if (timeframe === 'this_month') return { from: `${today.slice(0, 7)}-01`, to: today };
  if (timeframe === 'last_30_days') return { from: shiftDay(today, -29), to: today };

  return { from: mondayOf(today), to: today };
}

/**
 * How each kind of day is drawn.
 *
 * One map, so the strip, the donut and the legend cannot disagree. A day the
 * institute does not work — a week off, a declared holiday, or a day that has
 * not happened yet — is outlined rather than filled: it is not a day anybody
 * failed to sign in for.
 */
const DAY_TONE: Record<DayKind, string> = {
  PRESENT: 'bg-emerald-500 text-white',
  PENDING: 'bg-amber-500 text-white',
  REJECTED: 'bg-rose-500 text-white',
  ABSENT: 'bg-ink-3 text-surface',
  // Filled, not outlined: a granted day is a day the institute planned around,
  // and is not the same thing as a day nobody worked.
  LEAVE: 'bg-indigo-500 text-white',
  WEEK_OFF: 'bg-surface-3 text-ink-3 border border-line',
  HOLIDAY: 'bg-surface-3 text-ink-3 border border-line',
  UPCOMING: 'bg-surface-3 text-ink-3 border border-line',
};

export function TeacherAttendanceAnalyticsCard() {
  const [timeframe, setTimeframe] = useState<Timeframe>('this_week');

  // The month the *institute* is in, decided in IST by the server rather than
  // read off the device's clock.
  const {
    data: thisMonth,
    isLoading,
    error,
    refetch,
  } = useApiQuery(() => teacherApi.myStaffAttendance(), []);

  const previousMonth = thisMonth ? previousMonthOf(thisMonth.month) : null;
  const { data: lastMonth } = useApiQuery(
    () => (previousMonth ? teacherApi.myStaffAttendance(previousMonth) : Promise.resolve(null)),
    [previousMonth],
  );

  const today = thisMonth?.today.date ?? '';

  const days: StaffAttendanceDay[] = useMemo(
    () => [...(lastMonth?.days ?? []), ...(thisMonth?.days ?? [])],
    [lastMonth, thisMonth],
  );

  const stats = useMemo(() => {
    if (!today) return { workingDays: 0, present: 0, pending: 0, rejected: 0, absent: 0, leave: 0 };

    const { from, to } = windowOf(timeframe, today);
    const inWindow = days.filter((day) => day.date >= from && day.date <= to);
    const countOf = (kind: DayKind) => inWindow.filter((day) => day.kind === kind).length;

    const present = countOf('PRESENT');
    const pending = countOf('PENDING');
    const rejected = countOf('REJECTED');
    const absent = countOf('ABSENT');
    const leave = countOf('LEAVE');

    return {
      // Days the institute actually worked in this window: a week off and a
      // declared holiday are not days anybody signs in for. A day on granted
      // leave is still a day the institute worked — leaving it out shrank the
      // count and lifted the percentage for anybody who had taken leave.
      workingDays: present + pending + rejected + absent + leave,
      present,
      pending,
      rejected,
      absent,
      leave,
    };
  }, [days, timeframe, today]);

  const lastSevenDays = useMemo(() => {
    if (!today) return [];
    const from = shiftDay(today, -6);

    return days.filter((day) => day.date >= from && day.date <= today);
  }, [days, today]);

  /** Of the days worked. Zero working days is no percentage, not a zero one. */
  const attendanceRate =
    stats.workingDays > 0 ? Math.round((stats.present / stats.workingDays) * 100) : null;

  const share = (count: number) =>
    stats.workingDays > 0 ? (count / stats.workingDays) * 100 : 0;

  const presentShare = share(stats.present);
  const pendingShare = share(stats.pending);
  const rejectedShare = share(stats.rejected);
  const absentShare = share(stats.absent);
  const leaveShare = share(stats.leave);

  return (
    <div className="card bg-surface border border-line rounded-[16px] p-5 shadow-2xs flex flex-col gap-4">
      {/* Top Card Header with Dropdown */}
      <div className="flex items-center justify-between border-b border-line pb-3">
        <h3 className="text-base font-bold text-ink tracking-tight">My Attendance</h3>

        <Select
          id="my-attendance-timeframe"
          label="Choose the period to count"
          hideLabel
          options={TIMEFRAME_OPTIONS}
          value={timeframe}
          onChange={(event) => setTimeframe(event.target.value as Timeframe)}
          className="h-8 w-36 px-2.5 text-xs font-semibold"
        />
      </div>

      {isLoading && <SkeletonLines count={6} />}

      {!isLoading && error && (
        <div className="flex flex-col items-start gap-3">
          <Alert tone="critical" title="Could not load your attendance">
            {error.message}
          </Alert>
          <Button variant="secondary" size="sm" onClick={refetch}>
            Try again
          </Button>
        </div>
      )}

      {!isLoading && !error && (
        <div className="flex flex-col gap-3.5">
          {/* Header Row across full card width — Date aligns to right corner */}
          <div className="flex items-center justify-between text-xs">
            <span className="font-bold text-sm text-ink">Last 7 Days</span>
            <span className="text-xs text-ink-3 font-medium text-right">
              {today && `${formatInstituteDate(shiftDay(today, -6))} - ${formatInstituteDate(today)}`}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 items-center gap-6">
            {/* Left Area (7-Cols) */}
            <div className="md:col-span-7 flex flex-col gap-3.5">
              {/* Day Status Badges (S M T W T F S) */}
              <div className="flex items-center gap-2 pt-1 pb-1">
                {lastSevenDays.map((day) => {
                  const isToday = day.date === today;
                  return (
                    <span
                      key={day.date}
                      title={`${isToday ? 'Today · ' : ''}${formatInstituteDate(day.date)} — ${day.holidayName ?? day.kind.toLowerCase().replace('_', ' ')}`}
                      className={cn(
                        'h-8 w-8 rounded-[8px] flex items-center justify-center text-xs font-bold transition-all shadow-2xs relative',
                        DAY_TONE[day.kind],
                        isToday && day.kind === 'UPCOMING' && 'bg-indigo-600 text-white border-transparent',
                      )}
                    >
                      {day.weekday.charAt(0)}
                    </span>
                  );
                })}
              </div>

            {/* Working Days Total Line */}
            <div className="flex items-center gap-2 text-xs font-medium text-ink-2 mt-1">
              <Calendar className="h-3.5 w-3.5 text-accent shrink-0" />
              <span className="text-[12px]">
                No. of working days:{' '}
                <strong className="text-ink font-bold">{stats.workingDays} Days</strong>
              </span>
            </div>

            {/* 5-Stat Numbers Grid (Present, Pending, Rejected, Absent, Leave) */}
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 pt-2">
              <div>
                <p className="text-2xs text-ink-3 font-bold uppercase tracking-wider">Present</p>
                <p className="text-2xl font-black text-emerald-500 mt-0.5">{stats.present}</p>
              </div>
              <div>
                <p className="text-2xs text-ink-3 font-bold uppercase tracking-wider">Pending</p>
                <p className="text-2xl font-black text-amber-500 mt-0.5">{stats.pending}</p>
              </div>
              <div>
                <p className="text-2xs text-ink-3 font-bold uppercase tracking-wider">Rejected</p>
                <p className="text-2xl font-black text-rose-500 mt-0.5">{stats.rejected}</p>
              </div>
              <div>
                <p className="text-2xs text-ink-3 font-bold uppercase tracking-wider">Absent</p>
                <p className="text-2xl font-black text-ink mt-0.5">{stats.absent}</p>
              </div>
              <div>
                <p className="text-2xs text-ink-3 font-bold uppercase tracking-wider">Leave</p>
                <p className="text-2xl font-black text-indigo-500 mt-0.5">{stats.leave}</p>
              </div>
            </div>
          </div>

          {/* Right Area (5-Cols Donut Ring) */}
          <div className="md:col-span-5 flex flex-col items-center justify-center">
            <div className="relative h-36 w-36 flex items-center justify-center">
              <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90" role="img" aria-label={`Present on ${stats.present} of ${stats.workingDays} working days`}>
                <path
                  className="text-surface-3 stroke-current"
                  strokeWidth="3.5"
                  fill="none"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                {presentShare > 0 && (
                  <path
                    className="text-emerald-500 stroke-current"
                    strokeDasharray={`${presentShare}, 100`}
                    strokeWidth="3.5"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                )}
                {pendingShare > 0 && (
                  <path
                    className="text-amber-500 stroke-current"
                    strokeDasharray={`${pendingShare}, 100`}
                    strokeDashoffset={-presentShare}
                    strokeWidth="3.5"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                )}
                {rejectedShare > 0 && (
                  <path
                    className="text-rose-500 stroke-current"
                    strokeDasharray={`${rejectedShare}, 100`}
                    strokeDashoffset={-(presentShare + pendingShare)}
                    strokeWidth="3.5"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                )}
                {absentShare > 0 && (
                  <path
                    className="text-slate-500 dark:text-slate-600 stroke-current"
                    strokeDasharray={`${absentShare}, 100`}
                    strokeDashoffset={-(presentShare + pendingShare + rejectedShare)}
                    strokeWidth="3.5"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                )}
                {leaveShare > 0 && (
                  <path
                    className="text-indigo-500 stroke-current"
                    strokeDasharray={`${leaveShare}, 100`}
                    strokeDashoffset={-(presentShare + pendingShare + rejectedShare + absentShare)}
                    strokeWidth="3.5"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                )}
              </svg>

              {/* Center Donut Label */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
                <span className="text-2xl font-black text-ink">
                  {attendanceRate === null ? '—' : `${attendanceRate}%`}
                </span>
                <span className="text-sm font-bold text-ink-3 uppercase tracking-wider">Attendance</span>
              </div>
            </div>
          </div>
        </div>
        </div>
      )}
    </div>
  );
}
